import React, { useState, useEffect, useRef } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';

import appdata from '../../../../Services/appdata';
import { thousandFormat2 } from '../../../../Utils/Functions';
import { empty } from '../../../../Assets/js/constant';

const tableBorder = {
  borderBottom: '1px solid lightgrey',
  borderRight: '1px solid lightgrey',
  height: 20,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function BottomChart({
  detailData,
  selectedOption,
  selectedTopItems,
  tableStructure,
  profitName,
  pprofitName,
  showPercent,
  handleFilter,
}) {
  const _isMounted = useRef(false);
  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedIdRects, setSelectedIdRects] = useState([]);
  const [pageOffset, setPageOffset] = useState(0);
  const [filterStructure, setFilterStructure] = useState([]);
  const [profit, setProfit] = useState({});
  const [pprofit, setPprofit] = useState({});

  const onResize = () => {
    if (_isMounted.current === true) setResize(!resize);
  }

  useEffect(() => {
    _isMounted.current = true;
    prepareData();
    window.addEventListener('resize', onResize);
    return () => {
      _isMounted.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    prepareData();
  }, [detailData, selectedTopItems, tableStructure]);

  const prepareData = async () => {
    let profit = {
      incomeActual: 0,
      incomeBudget: 0,
      incomeForecast: 0,
      incomeActualVsBudgetVariance: 0,
      incomeActualVsForecastVariance: 0,
      incomeAnnualForecast: 0,
      incomeAnnualBudget: 0,
      incomeVariance: 0,
      expenseActual: 0,
      expenseBudget: 0,
      expenseForecast: 0,
      expenseActualVsBudgetVariance: 0,
      expenseActualVsForecastVariance: 0,
      expenseAnnualForecast: 0,
      expenseAnnualBudget: 0,
      expenseVariance: 0,
    };
    let pprofit = {};
    let alreadyIncluded = {
      Actual: 0,
      Budget: 0,
      Forecast: 0,
      ActualVsBudgetVariance: 0,
      ActualVsForecastVariance: 0,
      AnnualForecast: 0,
      AnnualBudget: 0,
      Variance: 0,
    };
    let filterStructure = [];
    let tableStructure = [];

    if (appdata.getItem('tableStructure'))
      tableStructure = appdata.getItem('tableStructure');
    
    if (detailData && Object.keys(detailData).length > 0) {
      let incomeExpenseData = detailData.IncomeExpense;
      let defaultData = detailData.Default;

      const incomeExpenseMap = new Map(
        incomeExpenseData.map(item => [item.IncomeStatementCategory, item])
      );    
      const mergedData = [...incomeExpenseData];

      // Check Default items and add missing ones to mergedData
      defaultData.forEach(defaultItem => {
        if (!incomeExpenseMap.has(defaultItem.IncomeStatementCategory)) {
          mergedData.push(defaultItem);
        }
      });
      
      tableStructure.forEach((structure) => {
        let filterAccount = structure;
        let subAccounts = structure.subItems;
        let removeAccounts = [];
        if (structure.total) {
          structure.subItems.forEach((item) => {
            let account = mergedData.find(
              (account) => account.IncomeStatementCategory === item.content,
            );
            if (account) {
              item.Actual = parseFloat(account.Actual);
              item.Budget = parseFloat(account.Budget);
              item.Forecast = parseFloat(account.Forecast);
              item.ActualVsBudgetVariance = parseFloat(
                account.ActualVsBudgetVariance,
              );
              item.ActualVsForecastVariance = parseFloat(
                account.ActualVsForecastVariance,
              );
              // item.AnnualForecast = parseFloat(account.AnnualForecast)
              item.AnnualForecast = parseFloat(account.YTDForecast) + parseFloat(account.YTDActual);
              item.AnnualBudget = parseFloat(account.YTDBudget);
              // item.Variance = structure.Income
              //   ? item.AnnualBudget  - parseFloat(item.AnnualForecast)
              //   : parseFloat(item.AnnualForecast) - item.AnnualBudget ;
              item.Income = account.Income;
            } else removeAccounts.push(item);
          });
          subAccounts = subAccounts.filter((e) => {
            return !removeAccounts.some((s) => {
              return s.content === e.content;
            });
          });
          filterAccount.subItems = subAccounts;
          if (subAccounts.length > 0) filterStructure.push(filterAccount);
        } else filterStructure.push(structure);
      });
      
      filterStructure.map((structure) => {
        if (structure.total) {
          structure.totalActual = 0;
          structure.totalBudget = 0;
          structure.totalForecast = 0;
          structure.totalActualVsForecastVariance = 0;
          structure.totalActualVsBudgetVariance = 0;
          structure.totalAnnualForecast = 0;
          structure.totalAnnualBudget = 0;
          structure.totalVariance = 0;
          if (structure.income) {
            structure.subItems.map((item) => {
              if (item.Income) {
                structure.totalActual += item.Actual;
                structure.totalBudget += item.Budget;
                structure.totalForecast += item.Forecast;
                structure.totalAnnualForecast += item.AnnualForecast;
                structure.totalAnnualBudget += item.AnnualBudget;
                item.Variance = item.AnnualForecast - item.AnnualBudget;
              } else {
                item.Actual = 0 - item.Actual;
                item.Budget = 0 - item.Budget;
                item.Forecast = 0 - item.Forecast;
                item.AnnualForecast = 0 - item.AnnualForecast;
                item.AnnualBudget = 0 - item.AnnualBudget;
                item.Variance = item.AnnualForecast - item.AnnualBudget;
                structure.totalActual += item.Actual;
                structure.totalBudget += item.Budget;
                structure.totalForecast += item.Forecast;
                structure.totalAnnualForecast += item.AnnualForecast;
                structure.totalAnnualBudget += item.AnnualBudget;
              }
              return item;
            });
            structure.totalActualVsForecastVariance =
              structure.totalActual - structure.totalForecast;
            structure.totalActualVsBudgetVariance =
              structure.totalActual - structure.totalBudget;
            structure.totalVariance =
              structure.totalAnnualForecast - structure.totalAnnualBudget;
          } else {
            structure.subItems.map((item) => {
              if (item.Income) {
                item.Actual = 0 - item.Actual;
                item.Budget = 0 - item.Budget;
                item.Forecast = 0 - item.Forecast;
                item.AnnualForecast = 0 - item.AnnualForecast;
                item.AnnualBudget = 0 - item.AnnualBudget;
                item.Variance = item.AnnualBudget - item.AnnualForecast;
                structure.totalActual += item.Actual;
                structure.totalBudget += item.Budget;
                structure.totalForecast += item.Forecast;
                structure.totalAnnualForecast += item.AnnualForecast;
                structure.totalAnnualBudget += item.AnnualBudget;
              } else {
                structure.totalActual += item.Actual;
                structure.totalBudget += item.Budget;
                structure.totalForecast += item.Forecast;
                item.Variance = item.AnnualBudget - item.AnnualForecast;
                structure.totalAnnualForecast += item.AnnualForecast;
                structure.totalAnnualBudget += item.AnnualBudget;
              }
              return item;
            });
            structure.totalActualVsForecastVariance =
              structure.totalForecast - structure.totalActual;
            structure.totalActualVsBudgetVariance =
              structure.totalBudget - structure.totalActual;
            structure.totalVariance =
              structure.totalAnnualBudget - structure.totalAnnualForecast;
          }
        }

        return structure;
      });
      
      let total_index = 0;
      for (var i = 0; i < filterStructure.length; i++) {
        if (filterStructure[i].total) {
          if (filterStructure[i].income) {
            profit.incomeActual += filterStructure[i].totalActual;
            profit.incomeBudget += filterStructure[i].totalBudget;
            profit.incomeForecast += filterStructure[i].totalForecast;
            profit.incomeActualVsBudgetVariance +=
              filterStructure[i].totalActual - filterStructure[i].totalBudget;
            profit.incomeActualVsForecastVariance +=
              filterStructure[i].totalActual - filterStructure[i].totalForecast;
            profit.incomeAnnualForecast +=
              filterStructure[i].totalAnnualForecast;
            profit.incomeAnnualBudget += filterStructure[i].totalAnnualBudget;
            profit.incomeVariance +=
              filterStructure[i].totalAnnualForecast -
              filterStructure[i].totalAnnualBudget;

            if (filterStructure[i].alreadyIncluded) {
              alreadyIncluded.Actual += filterStructure[i].totalActual;
              alreadyIncluded.Budget += filterStructure[i].totalBudget;
              alreadyIncluded.Forecast += filterStructure[i].totalForecast;
              alreadyIncluded.ActualVsBudgetVariance +=
                filterStructure[i].totalActual - filterStructure[i].totalBudget;
              alreadyIncluded.ActualVsForecastVariance +=
                filterStructure[i].totalActual -
                filterStructure[i].totalForecast;
              alreadyIncluded.AnnualForecast +=
                filterStructure[i].totalAnnualForecast;
              alreadyIncluded.AnnualBudget +=
                filterStructure[i].totalAnnualBudget;
              alreadyIncluded.Variance +=
                filterStructure[i].totalAnnualForecast -
                filterStructure[i].totalAnnualBudget;
            }
          } else {
            profit.expenseActual += filterStructure[i].totalActual;
            profit.expenseBudget += filterStructure[i].totalBudget;
            profit.expenseForecast += filterStructure[i].totalForecast;
            profit.expenseActualVsBudgetVariance +=
              filterStructure[i].totalActual - filterStructure[i].totalBudget;
            profit.expenseActualVsForecastVariance +=
              filterStructure[i].totalActual - filterStructure[i].totalForecast;
            profit.expenseAnnualForecast +=
              filterStructure[i].totalAnnualForecast;
            profit.expenseAnnualBudget += filterStructure[i].totalAnnualBudget;
            profit.expenseVariance +=
              filterStructure[i].totalAnnualForecast -
              filterStructure[i].totalAnnualBudget;
          }
        } else {
          filterStructure[i].profit = {
            incomeActual: 0,
            incomeBudget: 0,
            incomeForecast: 0,
            incomeActualVsBudgetVariance: 0,
            incomeActualVsForecastVariance: 0,
            incomeAnnualForecast: 0,
            incomeAnnualBudget: 0,
            incomeVariance: 0,
            expenseActual: 0,
            expenseBudget: 0,
            expenseForecast: 0,
            expenseActualVsBudgetVariance: 0,
            expenseActualVsForecastVariance: 0,
            expenseAnnualForecast: 0,
            expenseAnnualBudget: 0,
            expenseVariance: 0,
          };
          filterStructure[i].pprofit = {
            Actual: 0,
            Budget: 0,
            Forecast: 0,
            ActualVsBudgetVariance: 0,
            ActualVsForecastVariance: 0,
            AnnualForecast: 0,
            AnnualBudget: 0,
            Variance: 0,
          };
          for (var j = total_index; j < i; j++) {
            if (filterStructure[j].income) {
              filterStructure[i].profit.incomeActual +=
                filterStructure[j].totalActual;
              filterStructure[i].profit.incomeBudget +=
                filterStructure[j].totalBudget;
              filterStructure[i].profit.incomeForecast +=
                filterStructure[j].totalForecast;
              filterStructure[i].profit.incomeActualVsBudgetVariance +=
                filterStructure[j].totalActual - filterStructure[j].totalBudget;
              filterStructure[i].profit.incomeActualVsForecastVariance +=
                filterStructure[j].totalActual -
                filterStructure[j].totalForecast;
              filterStructure[i].profit.incomeAnnualForecast +=
                filterStructure[j].totalAnnualForecast;
              filterStructure[i].profit.incomeAnnualBudget +=
                filterStructure[j].totalAnnualBudget;
              filterStructure[i].profit.incomeVariance +=
                filterStructure[j].totalAnnualForecast -
                filterStructure[j].totalAnnualBudget;
            } else {
              filterStructure[i].profit.expenseActual +=
                filterStructure[j].totalActual;
              filterStructure[i].profit.expenseBudget +=
                filterStructure[j].totalBudget;
              filterStructure[i].profit.expenseForecast +=
                filterStructure[j].totalForecast;
              filterStructure[i].profit.expenseActualVsBudgetVariance +=
                filterStructure[j].totalActual - filterStructure[j].totalBudget;
              filterStructure[i].profit.expenseActualVsForecastVariance +=
                filterStructure[j].totalActual -
                filterStructure[j].totalForecast;
              filterStructure[i].profit.expenseAnnualForecast +=
                filterStructure[j].totalAnnualForecast;
              filterStructure[i].profit.expenseAnnualBudget +=
                filterStructure[j].totalAnnualBudget;
              filterStructure[i].profit.expenseVariance +=
                filterStructure[j].totalAnnualForecast -
                filterStructure[j].totalAnnualBudget;
            }
          }
          filterStructure[i].pprofit.Actual =
            filterStructure[i].profit.incomeActual !== 0
              ? Math.round(
                  ((filterStructure[i].profit.incomeActual -
                    filterStructure[i].profit.expenseActual) /
                    (filterStructure[i].profit.incomeActual -
                      alreadyIncluded.Actual)) *
                    100,
                )
              : 0;
          filterStructure[i].pprofit.Budget =
            filterStructure[i].profit.incomeBudget !== 0
              ? Math.round(
                  ((filterStructure[i].profit.incomeBudget -
                    filterStructure[i].profit.expenseBudget) /
                    (filterStructure[i].profit.incomeBudget -
                      alreadyIncluded.Budget)) *
                    100,
                )
              : 0;
          filterStructure[i].pprofit.Forecast =
            filterStructure[i].profit.incomeForecast !== 0
              ? Math.round(
                  ((filterStructure[i].profit.incomeForecast -
                    filterStructure[i].profit.expenseForecast) /
                    (filterStructure[i].profit.incomeForecast -
                      alreadyIncluded.Forecast)) *
                    100,
                )
              : 0;
          filterStructure[i].pprofit.ActualVsBudgetVariance =
            filterStructure[i].pprofit.Actual -
            filterStructure[i].pprofit.Budget;
          filterStructure[i].pprofit.ActualVsForecastVariance =
            filterStructure[i].pprofit.Actual -
            filterStructure[i].pprofit.Forecast;
          filterStructure[i].pprofit.AnnualForecast =
            filterStructure[i].profit.incomeAnnualForecast !== 0
              ? Math.round(
                  ((filterStructure[i].profit.incomeAnnualForecast -
                    filterStructure[i].profit.expenseAnnualForecast) /
                    (filterStructure[i].profit.incomeAnnualForecast -
                      alreadyIncluded.AnnualForecast)) *
                    100,
                )
              : 0;
          filterStructure[i].pprofit.AnnualBudget =
            filterStructure[i].profit.incomeAnnualBudget !== 0
              ? Math.round(
                  ((filterStructure[i].profit.incomeAnnualBudget -
                    filterStructure[i].profit.expenseAnnualBudget) /
                    (filterStructure[i].profit.incomeAnnualBudget -
                      alreadyIncluded.AnnualBudget)) *
                    100,
                )
              : 0;
          filterStructure[i].pprofit.Variance =
            filterStructure[i].pprofit.AnnualForecast -
            filterStructure[i].pprofit.AnnualBudget;

          total_index = i + 1;
        }
      }

      filterStructure.map((structure) => {
        if (structure.total) {
          structure.totalActualPercent =
            profit.incomeActual === 0
              ? 0
              : Math.round((structure.totalActual / profit.incomeActual) * 100);
          structure.totalBudgetPercent =
            profit.incomeBudget === 0
              ? 0
              : Math.round((structure.totalBudget / profit.incomeBudget) * 100);
          structure.totalForecastPercent =
            profit.incomeForecast === 0
              ? 0
              : Math.round(
                  (structure.totalForecast / profit.incomeForecast) * 100,
                );
          structure.totalAnnualForecastPercent =
            profit.incomeAnnualForecast === 0
              ? 0
              : Math.round(
                  (structure.totalAnnualForecast /
                    profit.incomeAnnualForecast) *
                    100,
                );
          structure.totalAnnualBudgetPercent =
            profit.incomeAnnualBudget === 0
              ? 0
              : Math.round(
                  (structure.totalAnnualBudget / profit.incomeAnnualBudget) *
                    100,
                );
          structure.totalActualPercent =
            structure.totalActualPercent >= 0
              ? `${structure.totalActualPercent}%`
              : `(${Math.abs(structure.totalActualPercent)}%)`;
          structure.totalBudgetPercent =
            structure.totalBudgetPercent >= 0
              ? `${structure.totalBudgetPercent}%`
              : `(${Math.abs(structure.totalBudgetPercent)}%)`;
          structure.totalForecastPercent =
            structure.totalForecastPercent >= 0
              ? `${structure.totalForecastPercent}%`
              : `(${Math.abs(structure.totalForecastPercent)}%)`;
          structure.totalAnnualForecastPercent =
            structure.totalAnnualForecastPercent >= 0
              ? `${structure.totalAnnualForecastPercent}%`
              : `(${Math.abs(structure.totalAnnualForecastPercent)}%)`;
          structure.totalAnnualBudgetPercent =
            structure.totalAnnualBudgetPercent >= 0
              ? `${structure.totalAnnualBudgetPercent}%`
              : `(${Math.abs(structure.totalAnnualBudgetPercent)}%)`;
        }

        return structure;
      });

      pprofit.Actual =
        profit.incomeActual !== 0
          ? Math.round(
              ((profit.incomeActual - profit.expenseActual) /
                (profit.incomeActual - alreadyIncluded.Actual)) *
                100,
            )
          : 0;
      pprofit.Budget =
        profit.incomeBudget !== 0
          ? Math.round(
              ((profit.incomeBudget - profit.expenseBudget) /
                (profit.incomeBudget - alreadyIncluded.Budget)) *
                100,
            )
          : 0;
      pprofit.Forecast =
        profit.incomeForecast !== 0
          ? Math.round(
              ((profit.incomeForecast - profit.expenseForecast) /
                (profit.incomeForecast - alreadyIncluded.Forecast)) *
                100,
            )
          : 0;
      pprofit.ActualVsBudgetVariance = pprofit.Actual - pprofit.Budget;
      pprofit.ActualVsForecastVariance = pprofit.Actual - pprofit.Forecast;
      pprofit.AnnualForecast =
        profit.incomeAnnualForecast !== 0
          ? Math.round(
              ((profit.incomeAnnualForecast - profit.expenseAnnualForecast) /
                (profit.incomeAnnualForecast -
                  alreadyIncluded.AnnualForecast)) *
                100,
            )
          : 0;
      pprofit.AnnualBudget =
        profit.incomeAnnualBudget !== 0
          ? Math.round(
              ((profit.incomeAnnualBudget - profit.expenseAnnualBudget) /
                (profit.incomeAnnualBudget - alreadyIncluded.AnnualBudget)) *
                100,
            )
          : 0;
      pprofit.Variance = pprofit.AnnualForecast - pprofit.AnnualBudget;
      
      setFilterStructure(filterStructure);
      setProfit(profit);
      setPprofit(pprofit);
    }

    deSelectAll();
  };

  const handleChangeShowPercent = (event) => {
    handleFilter({
      showPercent: event.target.checked,
    });
  };

  const handleChange = (event) => {
    handleFilter({
      selectedOption: event.target.value,
    });
  };

  const handleElement = (event, element, data) => {
    let _selectedIdRects = [];
    let exist = false,
      index = NaN;

    switch (element) {
      case 'table':
        if (event.shiftKey) {
          _selectedIdRects = selectedIdRects.slice();

          for (let i = 0; i < selectedIdRects.length; i++) {
            if (selectedIdRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedIdRects.push(event.target);
          } else {
            event.target.classList.remove('bkgActive');
            _selectedIdRects.splice(index, 1);
          }
        } else {
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
            if (rect === event.target) exist = true;
          });

          if (exist && selectedIdRects.length === 1) {
            _selectedIdRects = [];
          } else {
            event.target.classList.add('bkgActive');

            _selectedIdRects = [event.target];
          }
        }
        break;

      default:
        break;
    }

    setSelectedIdRects(_selectedIdRects);
  };

  const deSelectAll = () => {
    selectedIdRects.forEach((rect) => {
      rect.classList.remove('bkgActive');
    });

    setSelectedIdRects([]);
  };

  const handlePos = (event) => {
    const _pos =
      event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (pageOffset !== _pos && _isMounted.current === true) {
      setPageOffset(_pos);
    }
  };

  const handleChangeProfitName = (event, type) => {
    if (type === 'profit')
      handleFilter({
        profitName: profitName,
      });
    else
      handleFilter({
        pprofitName: pprofitName,
      });
  };

  return (
    <div
      style={{
        marginTop: 20,
      }}
      onMouseMove={(event) => handlePos(event)}
      onTouchMove={(event) => handlePos(event)}
    >
      <div style={{ paddingBottom: 10 }}>
        <FormControlLabel
          control={
            <FormControl variant="standard">
              <Select
                onChange={(event) => handleChange(event)}
                sx={{
                  '& .MuiInputBase-input': {
                    padding: 0,
                    paddingRight: 24,
                    fontWeight: 'bold',
                    fontFamily: 'Verdana !important',
                    fontSize: '10pt',
                  },
                }}
                value={selectedOption}
              >
                <MenuItem value="Forecast">Forecast</MenuItem>
                <MenuItem value="Budget">Budget</MenuItem>
              </Select>
            </FormControl>
          }
          label="Profit / Loss vs&nbsp;"
          labelPlacement="start"
          sx={{
            marginLeft: '0px !important',
            '& .MuiTypography-root': {
              fontWeight: 'bold',
              fontFamily: 'Verdana !important',
              fontSize: '10pt',
            },
          }}
        />
        <FormControlLabel
          id="pfp_percent"
          control={
            <Checkbox
              checked={showPercent}
              onChange={handleChangeShowPercent}
              color="success"
            />
          }
          label="Show %"
          labelPlacement="start"
          sx={{
            '& .MuiFormControlLabel-label': {
              paddingTop: '2px',
              fontWeight: 'bold',
              fontFamily: 'Verdana !important',
              fontSize: '10pt',
            },
            '& .MuiCheckbox-colorPrimary': {
              color: '#1976d2',
            },
          }}
        />
      </div>

      {filterStructure.length > 0 ? (
        <div
          id="pfp_bottom"
          className="well"
          style={{
            overflow: 'auto',
            maxHeight: '506px',
            overflowX: 'hidden',
          }}
        >
          <Grid container style={{ fontSize: 12 }}>
            <Grid
              size={3}
              style={{ borderBottom: '1px solid lightgrey', height: 20 }}
            ></Grid>
            <Grid
              container
              size={9}
              style={{
                textAlign: 'center',
                borderBottom: '1px solid lightgrey',
                height: 20,
              }}
            >
              <div
                style={{
                  float: 'left',
                  width: showPercent ? 'calc(16.67% - 65px)' : '16.67%',
                }}
              >
                <b>MTD Actual</b>
              </div>
              {showPercent ? (
                <div
                  style={{
                    float: 'left',
                    width: 65,
                  }}
                >
                  <b>%</b>
                </div>
              ) : null}
              <div
                style={{
                  float: 'left',
                  width: showPercent ? 'calc(16.67% - 65px)' : '16.67%',
                }}
              >
                <b>
                  {selectedOption === 'Forecast'
                    ? 'MTD Forecast'
                    : 'MTD Budget'}
                </b>
              </div>
              {showPercent ? (
                <div
                  style={{
                    float: 'left',
                    width: 65,
                  }}
                >
                  <b>%</b>
                </div>
              ) : null}
              <div
                style={{
                  float: 'left',
                  width: '16.67%',
                }}
              >
                <b>MTD Variance</b>
              </div>
              <div
                style={{
                  float: 'left',
                  width: showPercent ? 'calc(16.67% - 65px)' : '16.67%',
                }}
              >
                <b>Annual Forecast</b>
              </div>
              {showPercent ? (
                <div
                  style={{
                    float: 'left',
                    width: 65,
                  }}
                >
                  <b>%</b>
                </div>
              ) : null}
              <div
                style={{
                  float: 'left',
                  width: showPercent ? 'calc(16.67% - 65px)' : '16.67%',
                }}
              >
                <b>Annual Budget</b>
              </div>
              {showPercent ? (
                <div
                  style={{
                    float: 'left',
                    width: 65,
                  }}
                >
                  <b>%</b>
                </div>
              ) : null}
              <div
                style={{
                  float: 'left',
                  width: '16.65%',
                }}
              >
                <b>Variance</b>
              </div>
            </Grid>
            {filterStructure.map((parent, pIndex) => {
              if (parent.total) {
                return (
                  <React.Fragment key={pIndex}>
                    <Grid size={3}>
                      <div>
                        {parent.total &&
                          parent.subItems.map((child, cIndex) => (
                            <Grid container key={cIndex}>
                              {cIndex === 0 ? (
                                <Grid
                                  size={4}
                                  style={{
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <b>{parent.content}</b>
                                </Grid>
                              ) : (
                                <Grid size={4}></Grid>
                              )}
                              <Grid
                                size={8}
                                style={{
                                  ...tableBorder,
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#ffffff'
                                      : cIndex % 2 === 0
                                      ? '#ffffff'
                                      : '#f7f7f7',
                                  justifyContent: 'start',
                                }}
                              >
                                {child.content}
                              </Grid>
                            </Grid>
                          ))}
                        <Grid container>
                          <Grid size={4}></Grid>
                          <Grid
                            size={8}
                            style={{
                              ...tableBorder,
                              backgroundColor: '#f7f7f7',
                              justifyContent: 'start',
                            }}
                          >
                            <b>{parent.totalTitle}</b>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                    <Grid
                      size={9}
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      <div>
                        {parent.total &&
                          parent.subItems.map((child, cIndex) => (
                            <Grid container key={cIndex}>
                              <div
                                style={{
                                  ...tableBorder,
                                  float: 'left',
                                  width: showPercent
                                    ? 'calc(16.67% - 65px)'
                                    : '16.67%',
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#ffffff'
                                      : cIndex % 2 === 0
                                      ? '#ffffff'
                                      : '#f7f7f7',
                                }}
                                onClick={(event) => {
                                  handleElement(event, 'table');
                                }}
                              >
                                {child.Actual >= 0
                                  ? thousandFormat2(child.Actual)
                                  : `(${thousandFormat2(
                                      Math.abs(child.Actual),
                                    )})`}
                              </div>
                              {showPercent ? (
                                <div
                                  style={{
                                    ...tableBorder,
                                    float: 'left',
                                    width: 65,
                                    backgroundColor:
                                      parent.subItems.length % 2 === 0
                                        ? cIndex % 2 === 0
                                          ? '#f7f7f7'
                                          : '#ffffff'
                                        : cIndex % 2 === 0
                                        ? '#ffffff'
                                        : '#f7f7f7',
                                  }}
                                >
                                  {profit.incomeActual === 0
                                    ? '0%'
                                    : Math.round(
                                        (child.Actual / profit.incomeActual) *
                                          100,
                                      ) >= 0
                                    ? `${Math.round(
                                        (child.Actual / profit.incomeActual) *
                                          100,
                                      )}%`
                                    : `(${Math.round(
                                        Math.abs(
                                          (child.Actual /
                                            profit.incomeActual) *
                                            100,
                                        ),
                                      )}%)`}
                                </div>
                              ) : null}
                              <div
                                style={{
                                  ...tableBorder,
                                  float: 'left',
                                  width: showPercent
                                    ? 'calc(16.67% - 65px)'
                                    : '16.67%',
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#ffffff'
                                      : cIndex % 2 === 0
                                      ? '#ffffff'
                                      : '#f7f7f7',
                                }}
                                onClick={(event) => {
                                  handleElement(event, 'table');
                                }}
                              >
                                {selectedOption === 'Forecast'
                                  ? child.Forecast >= 0
                                    ? thousandFormat2(child.Forecast)
                                    : `(${thousandFormat2(
                                        Math.abs(child.Forecast),
                                      )})`
                                  : child.Budget >= 0
                                  ? thousandFormat2(child.Budget)
                                  : `(${thousandFormat2(
                                      Math.abs(child.Budget),
                                    )})`}
                              </div>
                              {showPercent ? (
                                <div
                                  style={{
                                    ...tableBorder,
                                    float: 'left',
                                    width: 65,
                                    backgroundColor:
                                      parent.subItems.length % 2 === 0
                                        ? cIndex % 2 === 0
                                          ? '#f7f7f7'
                                          : '#ffffff'
                                        : cIndex % 2 === 0
                                        ? '#ffffff'
                                        : '#f7f7f7',
                                  }}
                                >
                                  {selectedOption === 'Forecast'
                                    ? profit.incomeForecast === 0
                                      ? '0%'
                                      : Math.round(
                                          (child.Forecast /
                                            profit.incomeForecast) *
                                            100,
                                        ) >= 0
                                      ? `${Math.round(
                                          (child.Forecast /
                                            profit.incomeForecast) *
                                            100,
                                        )}%`
                                      : `(${Math.round(
                                          Math.abs(
                                            (child.Forecast /
                                              profit.incomeForecast) *
                                              100,
                                          ),
                                        )}%)`
                                    : profit.incomeBudget === 0
                                    ? '0%'
                                    : Math.round(
                                        (child.Budget / profit.incomeBudget) *
                                          100,
                                      ) >= 0
                                    ? `${Math.round(
                                        (child.Budget / profit.incomeBudget) *
                                          100,
                                      )}%`
                                    : `(${Math.round(
                                        Math.abs(
                                          (child.Budget /
                                            profit.incomeBudget) *
                                            100,
                                        ),
                                      )}%)`}
                                </div>
                              ) : null}
                              <div
                                style={{
                                  ...tableBorder,
                                  float: 'left',
                                  width: '16.67%',
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#ffffff'
                                      : cIndex % 2 === 0
                                      ? '#ffffff'
                                      : '#f7f7f7',
                                  color:
                                    selectedOption === 'Forecast'
                                      ? child.ActualVsForecastVariance > 0
                                        ? '#389a38'
                                        : 'black'
                                      : child.ActualVsBudgetVariance > 0
                                      ? '#389a38'
                                      : 'black',
                                }}
                                onClick={(event) => {
                                  handleElement(event, 'table');
                                }}
                              >
                                {selectedOption === 'Forecast'
                                  ? child.ActualVsForecastVariance >= 0
                                    ? thousandFormat2(
                                        child.ActualVsForecastVariance,
                                      )
                                    : `(${thousandFormat2(
                                        Math.abs(
                                          child.ActualVsForecastVariance,
                                        ),
                                      )})`
                                  : child.ActualVsBudgetVariance >= 0
                                  ? thousandFormat2(
                                      child.ActualVsBudgetVariance,
                                    )
                                  : `(${thousandFormat2(
                                      Math.abs(child.ActualVsBudgetVariance),
                                    )})`}
                              </div>
                              <div
                                style={{
                                  ...tableBorder,
                                  float: 'left',
                                  width: showPercent
                                    ? 'calc(16.67% - 65px)'
                                    : '16.67%',
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#f0f0f0'
                                      : cIndex % 2 === 0
                                      ? '#f0f0f0'
                                      : '#f7f7f7',
                                }}
                                onClick={(event) => {
                                  handleElement(event, 'table');
                                }}
                              >
                                {child.AnnualForecast >= 0
                                  ? thousandFormat2(child.AnnualForecast)
                                  : `(${thousandFormat2(
                                      Math.abs(child.AnnualForecast),
                                    )})`}
                              </div>
                              {showPercent ? (
                                <div
                                  style={{
                                    ...tableBorder,
                                    float: 'left',
                                    width: 65,
                                    backgroundColor:
                                      parent.subItems.length % 2 === 0
                                        ? cIndex % 2 === 0
                                          ? '#f7f7f7'
                                          : '#f0f0f0'
                                        : cIndex % 2 === 0
                                        ? '#f0f0f0'
                                        : '#f7f7f7',
                                  }}
                                >
                                  {profit.incomeAnnualForecast === 0
                                    ? '0%'
                                    : Math.round(
                                        (child.AnnualForecast /
                                          profit.incomeAnnualForecast) *
                                          100,
                                      ) >= 0
                                    ? `${Math.round(
                                        (child.AnnualForecast /
                                          profit.incomeAnnualForecast) *
                                          100,
                                      )}%`
                                    : `(${Math.round(
                                        (Math.abs(child.AnnualForecast) /
                                          profit.incomeAnnualForecast) *
                                          100,
                                      )}%)`}
                                </div>
                              ) : null}
                              <div
                                style={{
                                  ...tableBorder,
                                  float: 'left',
                                  width: showPercent
                                    ? 'calc(16.67% - 65px)'
                                    : '16.67%',
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#f0f0f0'
                                      : cIndex % 2 === 0
                                      ? '#f0f0f0'
                                      : '#f7f7f7',
                                }}
                                onClick={(event) => {
                                  handleElement(event, 'table');
                                }}
                              >
                                {child.AnnualBudget >= 0
                                  ? thousandFormat2(child.AnnualBudget)
                                  : `(${thousandFormat2(
                                      Math.abs(child.AnnualBudget),
                                    )})`}
                              </div>
                              {showPercent ? (
                                <div
                                  style={{
                                    ...tableBorder,
                                    float: 'left',
                                    width: 65,
                                    backgroundColor:
                                      parent.subItems.length % 2 === 0
                                        ? cIndex % 2 === 0
                                          ? '#f7f7f7'
                                          : '#f0f0f0'
                                        : cIndex % 2 === 0
                                        ? '#f0f0f0'
                                        : '#f7f7f7',
                                  }}
                                >
                                  {profit.incomeAnnualBudget === 0
                                    ? '0%'
                                    : Math.round(
                                        (child.AnnualBudget /
                                          profit.incomeAnnualBudget) *
                                          100,
                                      ) >= 0
                                    ? `${Math.round(
                                        (child.AnnualBudget /
                                          profit.incomeAnnualBudget) *
                                          100,
                                      )}%`
                                    : `(${Math.round(
                                        (Math.abs(child.AnnualBudget) /
                                          profit.incomeAnnualBudget) *
                                          100,
                                      )}%)`}
                                </div>
                              ) : null}
                              <div
                                style={{
                                  ...tableBorder,
                                  float: 'left',
                                  width: '16.65%',
                                  backgroundColor:
                                    parent.subItems.length % 2 === 0
                                      ? cIndex % 2 === 0
                                        ? '#f7f7f7'
                                        : '#f0f0f0'
                                      : cIndex % 2 === 0
                                      ? '#f0f0f0'
                                      : '#f7f7f7',
                                  color:
                                    child.Variance > 0 ? '#389a38' : 'black',
                                }}
                                onClick={(event) => {
                                  handleElement(event, 'table');
                                }}
                              >
                                {child.Variance >= 0
                                  ? thousandFormat2(child.Variance)
                                  : `(${thousandFormat2(
                                      Math.abs(child.Variance),
                                    )})`}
                              </div>
                            </Grid>
                          ))}

                        <Grid container>
                          <div
                            style={{
                              ...tableBorder,
                              float: 'left',
                              width: showPercent
                                ? 'calc(16.67% - 65px)'
                                : '16.67%',
                              backgroundColor: '#f7f7f7',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                          >
                            <b>
                              {parent.totalActual >= 0
                                ? thousandFormat2(parent.totalActual)
                                : `(${thousandFormat2(
                                    Math.abs(parent.totalActual),
                                  )})`}
                            </b>
                          </div>
                          {showPercent ? (
                            <div
                              style={{
                                ...tableBorder,
                                float: 'left',
                                width: 65,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <b>{parent.totalActualPercent}</b>
                            </div>
                          ) : null}
                          <div
                            style={{
                              ...tableBorder,
                              float: 'left',
                              width: showPercent
                                ? 'calc(16.67% - 65px)'
                                : '16.67%',
                              backgroundColor: '#f7f7f7',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                          >
                            <b>
                              {selectedOption === 'Forecast'
                                ? parent.totalForecast >= 0
                                  ? thousandFormat2(parent.totalForecast)
                                  : `(${thousandFormat2(
                                      Math.abs(parent.totalForecast),
                                    )})`
                                : parent.totalBudget >= 0
                                ? thousandFormat2(parent.totalBudget)
                                : `(${thousandFormat2(
                                    Math.abs(parent.totalBudget),
                                  )})`}
                            </b>
                          </div>
                          {showPercent ? (
                            <div
                              style={{
                                ...tableBorder,
                                float: 'left',
                                width: 65,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <b>{parent.totalBudgetPercent}</b>
                            </div>
                          ) : null}
                          <div
                            style={{
                              ...tableBorder,
                              float: 'left',
                              width: '16.67%',
                              backgroundColor: '#f7f7f7',
                              color:
                                selectedOption === 'Forecast'
                                  ? parent.totalActualVsForecastVariance > 0
                                    ? '#389a38'
                                    : 'black'
                                  : parent.totalActualVsBudgetVariance > 0
                                  ? '#389a38'
                                  : 'black',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                          >
                            <b>
                              {selectedOption === 'Forecast'
                                ? parent.totalActualVsForecastVariance >= 0
                                  ? thousandFormat2(
                                      parent.totalActualVsForecastVariance,
                                    )
                                  : `(${thousandFormat2(
                                      Math.abs(
                                        parent.totalActualVsForecastVariance,
                                      ),
                                    )})`
                                : parent.totalActualVsBudgetVariance >= 0
                                ? thousandFormat2(
                                    parent.totalActualVsBudgetVariance,
                                  )
                                : `(${thousandFormat2(
                                    Math.abs(
                                      parent.totalActualVsBudgetVariance,
                                    ),
                                  )})`}
                            </b>
                          </div>
                          <div
                            style={{
                              ...tableBorder,
                              float: 'left',
                              width: showPercent
                                ? 'calc(16.67% - 65px)'
                                : '16.67%',
                              backgroundColor: '#f7f7f7',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                          >
                            <b>
                              {parent.totalAnnualForecast >= 0
                                ? thousandFormat2(parent.totalAnnualForecast)
                                : `(${thousandFormat2(
                                    Math.abs(parent.totalAnnualForecast),
                                  )})`}
                            </b>
                          </div>
                          {showPercent ? (
                            <div
                              style={{
                                ...tableBorder,
                                float: 'left',
                                width: 65,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <b>{parent.totalAnnualForecastPercent}</b>
                            </div>
                          ) : null}
                          <div
                            style={{
                              ...tableBorder,
                              float: 'left',
                              width: showPercent
                                ? 'calc(16.67% - 65px)'
                                : '16.67%',
                              backgroundColor: '#f7f7f7',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                          >
                            <b>
                              {parent.totalAnnualBudget >= 0
                                ? thousandFormat2(parent.totalAnnualBudget)
                                : `(${thousandFormat2(
                                    Math.abs(parent.totalAnnualBudget),
                                  )})`}
                            </b>
                          </div>
                          {showPercent ? (
                            <div
                              style={{
                                ...tableBorder,
                                float: 'left',
                                width: 65,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <b>{parent.totalAnnualBudgetPercent}</b>
                            </div>
                          ) : null}
                          <div
                            style={{
                              ...tableBorder,
                              float: 'left',
                              width: '16.65%',
                              backgroundColor: '#f7f7f7',
                              color:
                                parent.totalVariance > 0
                                  ? '#389a38'
                                  : 'black',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                          >
                            <b>
                              {parent.totalVariance >= 0
                                ? thousandFormat2(parent.totalVariance)
                                : `(${thousandFormat2(
                                    Math.abs(parent.totalVariance),
                                  )})`}
                            </b>
                          </div>
                        </Grid>
                      </div>
                    </Grid>
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={pIndex}>
                    {/* Total profit */}

                    <Grid size={3}>
                      <Grid container>
                        <Grid
                          size={4}
                          style={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <b>{parent.content}</b>
                        </Grid>
                        <Grid
                          size={8}
                          style={{ ...tableBorder, justifyContent: 'start' }}
                        >
                          <b>Profit / Loss</b>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container size={9} style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          ...tableBorder,
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.profit.incomeActual -
                            parent.profit.expenseActual >=
                          0
                            ? thousandFormat2(
                                parent.profit.incomeActual -
                                  parent.profit.expenseActual,
                              )
                            : `(${thousandFormat2(
                                Math.abs(
                                  parent.profit.incomeActual -
                                    parent.profit.expenseActual,
                                ),
                              )})`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {selectedOption === 'Forecast'
                            ? parent.profit.incomeForecast -
                                parent.profit.expenseForecast >=
                              0
                              ? thousandFormat2(
                                  parent.profit.incomeForecast -
                                    parent.profit.expenseForecast,
                                )
                              : `(${thousandFormat2(
                                  Math.abs(
                                    parent.profit.incomeForecast -
                                      parent.profit.expenseForecast,
                                  ),
                                )})`
                            : parent.profit.incomeBudget -
                                parent.profit.expenseBudget >=
                              0
                            ? thousandFormat2(
                                parent.profit.incomeBudget -
                                  parent.profit.expenseBudget,
                              )
                            : `(${thousandFormat2(
                                Math.abs(
                                  parent.profit.incomeBudget -
                                    parent.profit.expenseBudget,
                                ),
                              )})`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          float: 'left',
                          width: '16.67%',
                          color:
                            selectedOption === 'Forecast'
                              ? parent.profit.incomeActualVsForecastVariance -
                                  parent.profit
                                    .expenseActualVsForecastVariance >
                                0
                                ? '#389a38'
                                : 'black'
                              : parent.profit.incomeActualVsBudgetVariance -
                                  parent.profit
                                    .expenseActualVsBudgetVariance >
                                0
                              ? '#389a38'
                              : 'black',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {selectedOption === 'Forecast'
                            ? parent.profit.incomeActualVsForecastVariance -
                                parent.profit
                                  .expenseActualVsForecastVariance >=
                              0
                              ? thousandFormat2(
                                  parent.profit
                                    .incomeActualVsForecastVariance -
                                    parent.profit
                                      .expenseActualVsForecastVariance,
                                )
                              : `(${thousandFormat2(
                                  Math.abs(
                                    parent.profit
                                      .incomeActualVsForecastVariance -
                                      parent.profit
                                        .expenseActualVsForecastVariance,
                                  ),
                                )})`
                            : parent.profit.incomeActualVsBudgetVariance -
                                parent.profit.expenseActualVsBudgetVariance >=
                              0
                            ? thousandFormat2(
                                parent.profit.incomeActualVsBudgetVariance -
                                  parent.profit.expenseActualVsBudgetVariance,
                              )
                            : `(${thousandFormat2(
                                Math.abs(
                                  parent.profit.incomeActualVsBudgetVariance -
                                    parent.profit
                                      .expenseActualVsBudgetVariance,
                                ),
                              )})`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f0f0f0',
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.profit.incomeAnnualForecast -
                            parent.profit.expenseAnnualForecast >=
                          0
                            ? thousandFormat2(
                                parent.profit.incomeAnnualForecast -
                                  parent.profit.expenseAnnualForecast,
                              )
                            : `(${thousandFormat2(
                                Math.abs(
                                  parent.profit.incomeAnnualForecast -
                                    parent.profit.expenseAnnualForecast,
                                ),
                              )})`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f0f0f0',
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.profit.incomeAnnualBudget -
                            parent.profit.expenseAnnualBudget >=
                          0
                            ? thousandFormat2(
                                parent.profit.incomeAnnualBudget -
                                  parent.profit.expenseAnnualBudget,
                              )
                            : `(${thousandFormat2(
                                Math.abs(
                                  parent.profit.incomeAnnualBudget -
                                    parent.profit.expenseAnnualBudget,
                                ),
                              )})`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f0f0f0',
                          float: 'left',
                          width: '16.65%',
                          color:
                            parent.profit.incomeVariance -
                              parent.profit.expenseVariance >
                            0
                              ? '#389a38'
                              : 'black',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.profit.incomeVariance -
                            parent.profit.expenseVariance >=
                          0
                            ? thousandFormat2(
                                parent.profit.incomeVariance -
                                  parent.profit.expenseVariance,
                              )
                            : `(${thousandFormat2(
                                Math.abs(
                                  parent.profit.incomeVariance -
                                    parent.profit.expenseVariance,
                                ),
                              )})`}
                        </b>
                      </div>
                    </Grid>

                    {/* Total %profit */}

                    <Grid size={3}>
                      <Grid container>
                        <Grid
                          size={4}
                          style={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <b>% {parent.content}</b>
                        </Grid>
                        <Grid
                          size={8}
                          style={{
                            ...tableBorder,
                            justifyContent: 'start',
                            backgroundColor: '#f7f7f7',
                          }}
                        >
                          <b>% Profit / Loss</b>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container size={9} style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f7f7f7',
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.pprofit.Actual >= 0
                            ? `${parent.pprofit.Actual}%`
                            : `(${Math.abs(parent.pprofit.Actual)}%)`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f7f7f7',
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {selectedOption === 'Forecast'
                            ? parent.pprofit.Forecast >= 0
                              ? `${parent.pprofit.Forecast}%`
                              : `(${Math.abs(parent.pprofit.Forecast)}%)`
                            : parent.pprofit.Budget >= 0
                            ? `${parent.pprofit.Budget}%`
                            : `(${Math.abs(parent.pprofit.Budget)}%)`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f7f7f7',
                          float: 'left',
                          width: '16.67%',
                          color:
                            selectedOption === 'Forecast'
                              ? parent.pprofit.ActualVsForecastVariance > 0
                                ? '#389a38'
                                : 'black'
                              : parent.pprofit.ActualVsBudgetVariance > 0
                              ? '#389a38'
                              : 'black',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {selectedOption === 'Forecast'
                            ? parent.pprofit.ActualVsForecastVariance >= 0
                              ? `${parent.pprofit.ActualVsForecastVariance}%`
                              : `(${Math.abs(
                                  parent.pprofit.ActualVsForecastVariance,
                                )}%)`
                            : parent.pprofit.ActualVsBudgetVariance >= 0
                            ? `${parent.pprofit.ActualVsBudgetVariance}%`
                            : `(${Math.abs(
                                parent.pprofit.ActualVsBudgetVariance,
                              )}%)`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f7f7f7',
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.pprofit.AnnualForecast >= 0
                            ? `${parent.pprofit.AnnualForecast}%`
                            : `(${Math.abs(parent.pprofit.AnnualForecast)}%)`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f7f7f7',
                          float: 'left',
                          width: '16.67%',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.pprofit.AnnualBudget >= 0
                            ? `${parent.pprofit.AnnualBudget}%`
                            : `(${Math.abs(parent.pprofit.AnnualBudget)}%)`}
                        </b>
                      </div>
                      <div
                        style={{
                          ...tableBorder,
                          backgroundColor: '#f7f7f7',
                          float: 'left',
                          width: '16.65%',
                          color:
                            parent.pprofit.Variance > 0 ? '#389a38' : 'black',
                        }}
                        onClick={(event) => {
                          handleElement(event, 'table');
                        }}
                      >
                        <b>
                          {parent.pprofit.Variance >= 0
                            ? `${parent.pprofit.Variance}%`
                            : `(${Math.abs(parent.pprofit.Variance)}%)`}
                        </b>
                      </div>
                    </Grid>
                  </React.Fragment>
                );
              }
            })}

            <Grid size={12} style={{ height: 2 }}></Grid>

            {/* profit */}

            <Grid
              size={3}
              style={{
                ...tableBorder,
                borderTop: '1px solid lightgrey',
                justifyContent: 'start',
              }}
            >
              <TextField
                sx={{
                  height: 25,
                  '& .MuiInputBase-input': {
                    height: 25,
                    padding: 0,
                    fontWeight: 'bold',
                    fontSize: 12,
                    fontFamily: 'Verdana !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderLeft: 'none',
                  }
                }}
                value={profitName}
                onChange={(e) => handleChangeProfitName(e, 'profit')}
                fullWidth
              />
            </Grid>
            <Grid container size={9} style={{ textAlign: 'center' }}>
              <div
                style={{
                  ...tableBorder,
                  borderTop: '1px solid lightgrey',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {profit.incomeActual - profit.expenseActual >= 0
                    ? thousandFormat2(
                        profit.incomeActual - profit.expenseActual,
                      )
                    : `(${thousandFormat2(
                        Math.abs(profit.incomeActual - profit.expenseActual),
                      )})`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  borderTop: '1px solid lightgrey',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {selectedOption === 'Forecast'
                    ? profit.incomeForecast - profit.expenseForecast >= 0
                      ? thousandFormat2(
                          profit.incomeForecast - profit.expenseForecast,
                        )
                      : `(${thousandFormat2(
                          Math.abs(
                            profit.incomeForecast - profit.expenseForecast,
                          ),
                        )})`
                    : profit.incomeBudget - profit.expenseBudget >= 0
                    ? thousandFormat2(
                        profit.incomeBudget - profit.expenseBudget,
                      )
                    : `(${thousandFormat2(
                        Math.abs(profit.incomeBudget - profit.expenseBudget),
                      )})`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  borderTop: '1px solid lightgrey',
                  float: 'left',
                  width: '16.67%',
                  color:
                    selectedOption === 'Forecast'
                      ? profit.incomeActualVsForecastVariance -
                          profit.expenseActualVsForecastVariance >
                        0
                        ? '#389a38'
                        : 'black'
                      : profit.incomeActualVsBudgetVariance -
                          profit.expenseActualVsBudgetVariance >
                        0
                      ? '#389a38'
                      : 'black',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {selectedOption === 'Forecast'
                    ? profit.incomeActualVsForecastVariance -
                        profit.expenseActualVsForecastVariance >=
                      0
                      ? thousandFormat2(
                          profit.incomeActualVsForecastVariance -
                            profit.expenseActualVsForecastVariance,
                        )
                      : `(${thousandFormat2(
                          Math.abs(
                            profit.incomeActualVsForecastVariance -
                              profit.expenseActualVsForecastVariance,
                          ),
                        )})`
                    : profit.incomeActualVsBudgetVariance -
                        profit.expenseActualVsBudgetVariance >=
                      0
                    ? thousandFormat2(
                        profit.incomeActualVsBudgetVariance -
                          profit.expenseActualVsBudgetVariance,
                      )
                    : `(${thousandFormat2(
                        Math.abs(
                          profit.incomeActualVsBudgetVariance -
                            profit.expenseActualVsBudgetVariance,
                        ),
                      )})`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  borderTop: '1px solid lightgrey',
                  backgroundColor: '#f0f0f0',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {profit.incomeAnnualForecast -
                    profit.expenseAnnualForecast >=
                  0
                    ? thousandFormat2(
                        profit.incomeAnnualForecast -
                          profit.expenseAnnualForecast,
                      )
                    : `(${thousandFormat2(
                        Math.abs(
                          profit.incomeAnnualForecast -
                            profit.expenseAnnualForecast,
                        ),
                      )})`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  borderTop: '1px solid lightgrey',
                  backgroundColor: '#f0f0f0',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {profit.incomeAnnualBudget - profit.expenseAnnualBudget >= 0
                    ? thousandFormat2(
                        profit.incomeAnnualBudget -
                          profit.expenseAnnualBudget,
                      )
                    : `(${thousandFormat2(
                        Math.abs(
                          profit.incomeAnnualBudget -
                            profit.expenseAnnualBudget,
                        ),
                      )})`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  borderTop: '1px solid lightgrey',
                  backgroundColor: '#f0f0f0',
                  float: 'left',
                  width: '16.65%',
                  color:
                    profit.incomeVariance - profit.expenseVariance > 0
                      ? '#389a38'
                      : 'black',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {profit.incomeVariance - profit.expenseVariance >= 0
                    ? thousandFormat2(
                        profit.incomeVariance - profit.expenseVariance,
                      )
                    : `(${thousandFormat2(
                        Math.abs(
                          profit.incomeVariance - profit.expenseVariance,
                        ),
                      )})`}
                </b>
              </div>
            </Grid>

            {/* %profit */}

            <Grid
              size={3}
              style={{
                ...tableBorder,
                backgroundColor: '#f7f7f7',
                justifyContent: 'start',
              }}
            >
              <TextField
                sx={{
                  height: 25,
                  '& .MuiInputBase-input': {
                    height: 25,
                    padding: 0,
                    fontWeight: 'bold',
                    fontSize: 12,
                    fontFamily: 'Verdana !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderLeft: 'none',
                  },
                }}
                value={pprofitName}
                onChange={(e) => handleChangeProfitName(e, 'percent')}
                fullWidth
              />
            </Grid>
            <Grid container size={9} style={{ textAlign: 'center' }}>
              <div
                style={{
                  ...tableBorder,
                  backgroundColor: '#f7f7f7',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {pprofit.Actual >= 0
                    ? `${pprofit.Actual}%`
                    : `(${Math.abs(pprofit.Actual)}%)`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  backgroundColor: '#f7f7f7',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {selectedOption === 'Forecast'
                    ? pprofit.Forecast >= 0
                      ? `${pprofit.Forecast}%`
                      : `(${Math.abs(pprofit.Forecast)}%)`
                    : pprofit.Budget >= 0
                    ? `${pprofit.Budget}%`
                    : `(${Math.abs(pprofit.Budget)}%)`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  backgroundColor: '#f7f7f7',
                  float: 'left',
                  width: '16.67%',
                  color:
                    selectedOption === 'Forecast'
                      ? pprofit.ActualVsForecastVariance > 0
                        ? '#389a38'
                        : 'black'
                      : pprofit.ActualVsBudgetVariance > 0
                      ? '#389a38'
                      : 'black',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {selectedOption === 'Forecast'
                    ? pprofit.ActualVsForecastVariance >= 0
                      ? `${pprofit.ActualVsForecastVariance}%`
                      : `(${Math.abs(pprofit.ActualVsForecastVariance)}%)`
                    : pprofit.ActualVsBudgetVariance >= 0
                    ? `${pprofit.ActualVsBudgetVariance}%`
                    : `(${Math.abs(pprofit.ActualVsBudgetVariance)}%)`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  backgroundColor: '#f7f7f7',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {pprofit.AnnualForecast >= 0
                    ? `${pprofit.AnnualForecast}%`
                    : `(${Math.abs(pprofit.AnnualForecast)}%)`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  backgroundColor: '#f7f7f7',
                  float: 'left',
                  width: '16.67%',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {pprofit.AnnualBudget >= 0
                    ? `${pprofit.AnnualBudget}%`
                    : `(${Math.abs(pprofit.AnnualBudget)}%)`}
                </b>
              </div>
              <div
                style={{
                  ...tableBorder,
                  backgroundColor: '#f7f7f7',
                  float: 'left',
                  width: '16.65%',
                  color: pprofit.Variance > 0 ? '#389a38' : 'black',
                }}
                onClick={(event) => {
                  handleElement(event, 'table');
                }}
              >
                <b>
                  {pprofit.Variance >= 0
                    ? `${pprofit.Variance}%`
                    : `(${Math.abs(pprofit.Variance)}%)`}
                </b>
              </div>
            </Grid>
          </Grid>
        </div>
      ) : (
        <div style={empty(200)} className="well">
          No data
        </div>
      )}
    </div>
  );
}

BottomChart.propTypes = {
  detailData: PropTypes.object.isRequired,
  handleFilter: PropTypes.func.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedOption: PropTypes.string.isRequired,
  showPercent: PropTypes.bool.isRequired,
};

export default BottomChart;
